import type { Role } from '@nivex/contracts'
import type { useApiFetch } from '@/features/auth'
import { buildQueryString } from '@/lib/buildQueryString'

export interface AdminUserSummary {
  id: string
  email: string
  name: string | null
  role: Role | null
  /** Also used as the Plivo caller-id when this staff member places a call. */
  phone: string | null
  /** A live count of cases currently assigned to this staff member — drops on reassignment. */
  caseCount: number
  /** A lifetime count of calls this staff member has logged — never decremented. */
  callCount: number
  /** `false` once an admin has deactivated the account. A missing `StaffProfile` row (today's
   * reality for most staff) counts as active. */
  isActive: boolean
}

export interface AdminUsersListParams {
  page?: number
  pageSize?: number
  search?: string
}

export interface AdminUsersListResponse {
  items: AdminUserSummary[]
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export interface DeactivateStaffResult {
  deactivatedAt: string
  reassignedCaseCount: number
}

export interface ResetPasswordResult {
  password: string
}

export interface CreateStaffInput {
  name: string
  email: string
  phone: string
}

export interface CreateStaffResult {
  password: string
  emailSent: boolean
}

export interface UpdateStaffInput {
  name: string
  email: string
  phone: string
}

export interface SendLoginDetailsResult {
  password: string
  emailSent: boolean
}

export type UserAuditEventType =
  | 'CREATED'
  | 'UPDATED'
  | 'ROLE_CHANGED'
  | 'DEACTIVATED'
  | 'SYNC_FAILED'

export type UserChangeSource =
  | 'WEBHOOK'
  | 'CRON'
  | 'JIT'
  | 'MANUAL_SYNC'
  | 'ADMIN_ACTION'
  | 'SELF_SERVICE'

export interface UserAuditLogEntry {
  id: string
  eventType: UserAuditEventType
  source: UserChangeSource
  /** Only the fields that changed (or, for a deactivation, the identifying fields). */
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  errorMessage: string | null
  /** Who made the change (name, else email); `null` = the system (webhook, daily sync, sign-in). */
  actorName: string | null
  createdAt: string
}

export interface UserAuditLogResponse {
  /** Newest first, the 200 most recent. */
  events: UserAuditLogEntry[]
}

export type SyncRunSource = 'CRON' | 'MANUAL' | 'STARTUP_CATCHUP'
export type SyncRunStatus = 'SUCCESS' | 'FAILED'

export interface LastSyncRun {
  /** `startedAt` of the latest run (ISO); `null` if a sync has never run. */
  lastRunAt: string | null
  source: SyncRunSource | null
  status: SyncRunStatus | null
  usersChecked: number
  usersUpserted: number
  usersDeactivated: number
}

type AuthedFetch = ReturnType<typeof useApiFetch>

export function fetchAdminUsers(
  authedFetch: AuthedFetch,
  params: AdminUsersListParams = {},
): Promise<AdminUsersListResponse> {
  return authedFetch<AdminUsersListResponse>(`/api/v1/admin/users${buildQueryString(params)}`)
}

export function createStaff(
  authedFetch: AuthedFetch,
  input: CreateStaffInput,
): Promise<CreateStaffResult> {
  return authedFetch<CreateStaffResult>('/api/v1/admin/users', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateStaff(
  authedFetch: AuthedFetch,
  userId: string,
  input: UpdateStaffInput,
): Promise<AdminUserSummary> {
  return authedFetch<AdminUserSummary>(`/api/v1/admin/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function resetStaffPassword(
  authedFetch: AuthedFetch,
  userId: string,
): Promise<ResetPasswordResult> {
  return authedFetch<ResetPasswordResult>(`/api/v1/admin/users/${userId}/reset-password`, {
    method: 'POST',
  })
}

export function sendLoginDetails(
  authedFetch: AuthedFetch,
  userId: string,
): Promise<SendLoginDetailsResult> {
  return authedFetch<SendLoginDetailsResult>(`/api/v1/admin/users/${userId}/send-login-details`, {
    method: 'POST',
  })
}

export function deactivateStaff(
  authedFetch: AuthedFetch,
  userId: string,
): Promise<DeactivateStaffResult> {
  return authedFetch<DeactivateStaffResult>(`/api/v1/admin/users/${userId}`, {
    method: 'DELETE',
  })
}

export function fetchUserAuditLog(
  authedFetch: AuthedFetch,
  userId: string,
): Promise<UserAuditLogResponse> {
  return authedFetch<UserAuditLogResponse>(`/api/v1/admin/users/${userId}/audit-log`)
}

export function fetchLastSyncRun(authedFetch: AuthedFetch): Promise<LastSyncRun> {
  return authedFetch<LastSyncRun>('/api/v1/admin/users/sync/last-run')
}
